import { getBrowserStorage, readStorageJSON, removeStorageItem, writeStorageJSON } from './storage.js';
import { readApiErrorCode, readAuthErrorCode } from './client.js';

export const createCacheFetch = ({
  apiFetchRaw,
  storageArea = 'local',
  defaultStaleMs = 60000,
  defaultRevalidate = true,
  onMetricStart = null,
  onMetricEnd = null,
} = {}) => {
  if (typeof apiFetchRaw !== 'function') {
    throw new Error('createCacheFetch requires apiFetchRaw');
  }

  const memory = new Map();
  const inflight = new Map();
  const now = () => Date.now();

  const readStorage = (storageKey) =>
    readStorageJSON(storageKey, { area: storageArea, fallback: null });

  const writeStorage = (storageKey, value) =>
    writeStorageJSON(storageKey, value, { area: storageArea });

  const getCached = (key) => memory.get(key) || null;

  const setCached = (key, data, options = {}) => {
    const { persist = false, storageKey } = options;
    const entry = {
      ...(memory.get(key) || {}),
      data,
      fetchedAt: now(),
    };
    memory.set(key, entry);
    if (persist && storageKey) writeStorage(storageKey, entry);
    return entry;
  };

  const deleteCached = (key, options = {}) => {
    const { storageKey } = options;
    memory.delete(key);
    inflight.delete(key);
    if (storageKey) removeStorageItem(storageKey, { area: storageArea });
  };

  const clearCachedByPrefix = (prefix) => {
    const normalized = String(prefix || '');
    if (!normalized) return;

    Array.from(memory.keys()).forEach((key) => {
      if (String(key).startsWith(normalized)) memory.delete(key);
    });
    Array.from(inflight.keys()).forEach((key) => {
      if (String(key).startsWith(normalized)) inflight.delete(key);
    });
    try {
      const storage = getBrowserStorage(storageArea);
      if (!storage) return;
      Array.from({ length: storage.length }, (_, index) => storage.key(index))
        .filter((key) => key && String(key).startsWith(normalized))
        .forEach((key) => storage.removeItem(key));
    } catch {
      // Ignore storage cleanup failures; in-memory entries were already removed.
    }
  };

  const cacheFetch = async (path, options = {}) => {
    const {
      key,
      staleMs = defaultStaleMs,
      method = 'GET',
      body,
      headers = {},
      token,
      params,
      persist = false,
      storageKey,
      revalidate = defaultRevalidate,
      signal,
      skipMetrics = false,
      validateResponse = value => value,
      allowStaleOnError = true,
    } = options;

    if (!key) throw new Error('cacheFetch requires a unique key');

    let base = memory.get(key) || null;
    if (!base && persist && storageKey) {
      const stored = readStorage(storageKey);
      if (stored?.data) {
        try { validateResponse(stored.data); } catch (error) {
          deleteCached(key, { storageKey });
          throw error;
        }
        base = stored;
        memory.set(key, stored);
      }
    }

    if (base) {
      try { validateResponse(base.data); } catch (error) {
        deleteCached(key, { storageKey });
        throw error;
      }
    }

    const metric = !skipMetrics && onMetricStart
      ? onMetricStart({ path, method, source: 'cacheFetch' })
      : null;

    const fresh = base && (now() - (base.fetchedAt || 0) < staleMs);
    if (fresh || (base && !revalidate)) {
      if (metric && onMetricEnd) {
        onMetricEnd(metric, {
          status: 200,
          payload: base.data,
          cacheStatus: fresh ? 'memory-hit' : 'cache-hit',
        });
      }
      return base.data;
    }

    if (inflight.has(key)) {
      const data = await inflight.get(key);
      validateResponse(data);
      if (metric && onMetricEnd) {
        onMetricEnd(metric, { status: 200, payload: data, cacheStatus: 'deduped' });
      }
      return data;
    }

    const conditionalHeaders = { ...headers };
    if (base?.etag) conditionalHeaders['If-None-Match'] = base.etag;
    if (base?.lastModified) conditionalHeaders['If-Modified-Since'] = base.lastModified;

    const request = apiFetchRaw(path, {
      method,
      body,
      headers: conditionalHeaders,
      token,
      params,
      signal,
      skipMetrics: true,
    })
      .then(({ response, data }) => {
        if (response.status === 304 && base) {
          const next = { ...base, fetchedAt: now() };
          if (inflight.get(key) === request) {
            memory.set(key, next);
            if (persist && storageKey) writeStorage(storageKey, next);
          }
          return next.data;
        }

        if (!response.ok) {
          if (allowStaleOnError && base?.data) return base.data;
          const error = new Error(data?.message || response.statusText || 'Request failed');
          error.status = response.status;
          error.apiCode = readApiErrorCode(response, data);
          error.authCode = readAuthErrorCode(response, data);
          throw error;
        }

        validateResponse(data);

        const next = {
          data,
          fetchedAt: now(),
          etag: response.headers.get('ETag') || response.headers.get('Etag') || null,
          lastModified: response.headers.get('Last-Modified') || null,
        };
        // A scope clear can invalidate this request while it is in flight.
        // Return its result to its original caller, but never re-cache it.
        if (inflight.get(key) === request) {
          memory.set(key, next);
          if (persist && storageKey) writeStorage(storageKey, next);
        }
        return data;
      })
      .finally(() => {
        if (inflight.get(key) === request) inflight.delete(key);
      });

    inflight.set(key, request);

    if (base && revalidate && allowStaleOnError) {
      request.catch(() => {});
      if (metric && onMetricEnd) {
        onMetricEnd(metric, {
          status: 200,
          payload: base.data,
          cacheStatus: 'stale-hit',
        });
      }
      return base.data;
    }

    try {
      const data = await request;
      if (metric && onMetricEnd) {
        onMetricEnd(metric, { status: 200, payload: data, cacheStatus: 'network' });
      }
      return data;
    } catch (error) {
      if (metric && onMetricEnd) {
        onMetricEnd(metric, {
          status: error?.status ?? null,
          error,
          cacheStatus: 'network',
        });
      }
      throw error;
    }
  };

  return {
    cacheFetch,
    getCached,
    setCached,
    deleteCached,
    clearCachedByPrefix,
  };
};
